import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { api_url } from "../../constant/constant";
import { useNavigate } from "react-router-dom";
import "./LearningManager.css";
import { useTranslation } from "react-i18next";
import { showToast } from "../../Components/ShowToast/ToastNotification";
import { Modal, Button } from "react-bootstrap";
import { NavLink } from "react-router-dom";

const LearningManager = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setLearningManager] = useState([]);
  const [links, setLinks] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLearningManagerId, setSelectedLearningManagerId] =
    useState(null);

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(`${api_url}/learning-campaigns/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        showToast(t("learningmanagementdeletedsuccessfully"), "success");
        fetchLearningManager("");
        setShowModal(false);
      } else {
        if (response.status === 403) {
          const result = await response.json();
          let errorMessage = result?.message;
          showToast(errorMessage, "error");
          setShowModal(false);
        }
      }
    } catch (error) {
      setShowModal(false);
      showToast(t("somethingwentwrongpleaserefresh"), "error");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchValue !== "") {
      const filteredData = data.filter(
        (item) =>
          item.type.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.title_ar.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.title_en.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.start_date.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.start_time.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.end_date.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.video_seek.toLowerCase().includes(searchValue.toLowerCase())
      );
      setLearningManager(filteredData);
    } else {
      fetchLearningManager("");
    }
  }, [searchValue]);

  const fetchLearningManager = async (url) => {
    setLoading(true);
    let page = 1;
    if (url !== "") {
      const temp = url.split("?page=");
      page = temp[1];
    }

    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        api_url + "/learning-campaigns?page=" + page,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setLearningManager(result.learning_campaigns.data);
      setLinks(result.learning_campaigns.links);
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast(t("errorfetchingdata"), "error");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLearningManager("");
  }, []);

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "type",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("Type")}
          </div>
        ),
        colName: t("Type"),
      },
      {
        accessorKey: "title_ar",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("Titlear")}
          </div>
        ),
        colName: t("Titlear"),
      },
      {
        accessorKey: "title_en",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("Titleen")}
          </div>
        ),
        colName: t("Titleen"),
      },
      {
        accessorKey: "start_date",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("StartDate")}
          </div>
        ),
        colName: t("StartDate"),
      },
      {
        accessorKey: "start_time",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("StartTime")}
          </div>
        ),
        colName: t("StartTime"),
      },
      {
        accessorKey: "end_date",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("EndDate")}
          </div>
        ),
        colName: t("EndDate"),
      },
      {
        accessorKey: "video_seek",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("VideoSeek")}
          </div>
        ),
        colName: t("VideoSeek"),
      },
      {
        accessorKey: "success_rate",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_link.png" alt="Department Name" />
            {t("SuccessRate")}
          </div>
        ),
        colName: t("SuccessRate"),
      },
      {
        accessorKey: "id",
        header: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src="/images/fi_alert-octagon.png" alt="Source" />
            {t("Action")}
          </div>
        ),
        cell: ({ getValue }) => {
          const id = getValue();

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                   <button 
                                    onClick={() => { 
                                        handleSendEmail(id); 
                                        setSendEmail(true); 
                                    }} 
                                    style={{
                                        backgroundColor: "gray",
                                        color: "white",
                                        padding: "5px 10px",
                                        border: "none",
                                        borderRadius: "5px",
                                        cursor: "pointer"
                                    }}
>
                        {t('SendEmail')}
                    </button>
                </div> */}

              {/* <LcLesson LearningManager_id={id} />  */}

              {/* <LcUserModal LearningManager_id={id} />  */}
              {/* <EditLeraningManager id={id}  parentfetchLearningManager={fetchLearningManager}/> */}
              <div
                className="iconDel"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLearningManagerId(id);
                  setShowModal(true);
                }}
              >
                <img src="/images/fi_trash-2.png" alt="Delete" />
                {t("Delete")}
              </div>
            </div>
          );
        },
        colName: t("Action"),
      },
    ],
    []
  );

  // Create a table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleSurveyRowClick = (id) => {
    navigate(`/learningcompaignmanager/${id}/LearningCompaignDetail`);
  };

  return (
    <div className="p-4 mb-4">
      <div className="pageTitle mb-4">{t("AllLearningCampaigns")}</div>
      <div className="primaryBlueBox mb-4" id="p-5">
        <div className="row">
          <div className="col-md-4">
            <label className="form-label">{t("LearningCampaign")}</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("SearchLearningCampaign")}
                aria-label="Search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary btnSearchIcon"
                type="button"
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>
          <div className="col-md-1">
            <div className="vrLine"></div>
          </div>
          <div className="col-md-7">
            {/* <AddLearningManager parentfetchLearningManager={fetchLearningManager} /> */}
            <NavLink
              className="btnWhiteGradient mt-4"
              to="/Add-Learning-Manager"
            >
              <i class="bi bi-envelope me-2 mt-3 h6"></i>{" "}
              {t("AddLearningCampaign")}
            </NavLink>
          </div>
        </div>
      </div>

      {/* <div className="table-responsive">
                             <table className="table table-bordered table-hover">
                                 <thead className="thead-dark">
                                     {table.getHeaderGroups().map(headerGroup => (
                                         <tr key={headerGroup.id}>
                                             {headerGroup.headers.map(header => (
                                                 <th key={header.id}>
                                                     {flexRender(header.column.columnDef.header, header.getContext())}
                                                 </th>
                                             ))}
                                         </tr>
                                     ))}
                                 </thead>
                     
                                 <tbody>
                                     {table.getRowModel().rows.map(row => (
                                         <tr key={row.id}>
                                             {row.getVisibleCells().map(cell => (
                                                 <td key={cell.id}>
                                                     {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                 </td>
                                             ))}
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         </div> */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center my-5">
          <div
            className="spinner-border"
            role="status"
            style={{
              borderTopColor: "#6897CB",
              borderBottomColor: "#6897CB",
              borderLeftColor: "#6897CB",
              marginTop: "50px",
            }}
          ></div>
        </div>
      ) : (
        <div className="bg-light px-3 py-3 lessonsTableWrapper resTable overflow-auto">
          {window.innerWidth < 768 ? (
            table.getRowModel().rows.map((row) => (
              <div
                key={row.id}
                className="stacked-row"
                onClick={
                  row.id.includes("id") === false
                    ? () => handleSurveyRowClick(row.original.id)
                    : null
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="stacked-cell">
                    <strong className="me-1">
                      {cell.column.columnDef.colName}:
                    </strong>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <table className="table table-bordered table-hover">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="rowAnchor"
                        onClick={
                          cell.id.includes("id") === false
                            ? () => handleSurveyRowClick(row.original.id)
                            : null
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="navigationWrapper">
        <ul>
          {links.map((link, index) => (
            <li
              key={index}
              className={`${link.active ? " active " : ""} ${
                link.url === null ? " disabled " : ""
              }`}
              onClick={
                link.url !== null
                  ? () => fetchLearningManager(link.url)
                  : undefined
              }
            >
              <div dangerouslySetInnerHTML={{ __html: link.label }} />
            </li>
          ))}
        </ul>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Areyousureyouwanttodeletethislearningcampaign?")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(selectedLearningManagerId)}
            disabled={loading}
          >
            {loading ? t("Deleting...") : t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LearningManager;
